/**
 * NO03 - Fakturakopi Search Component
 * Search form with advanced search options
 */

import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    Box,
    Paper,
    TextField,
    Button,
    Collapse,
    Typography,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    ToggleButton,
    ToggleButtonGroup,
    Alert,
    Tooltip,
} from '@mui/material';
import { Search as SearchIcon, ExpandMore, ExpandLess } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/nb'; // Norwegian Bokmål locale
import { makeStyles } from 'tss-react/mui';
import { AppState } from '../../../store/rootReducer';
import * as actionsNO03 from '../../../store/actions/indexNO03';
import { NO03SearchParams } from '../../../models/NO03';
import FirmNavnId from '../../../models/FirmNavnId';

const RED = '#ab273b';
const RED_DARK = '#8e1f30';
const neutralTheme = createTheme({ palette: { primary: { main: '#424242' } } });

const useStyles = makeStyles()((theme) => ({
    searchContainer: {
        padding: theme.spacing(2, 2.5),
        margin: 0,
        backgroundColor: '#fff',
        borderBottom: '1px solid #e0e0e0',
        flexShrink: 0,
    },
    title: {
        marginBottom: theme.spacing(0.5),
        fontWeight: 500,
        fontSize: '1.1rem',
    },
    subtitle: {
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1.25),
        fontSize: '0.8rem',
    },
    firmaSelectBox: {
        marginBottom: theme.spacing(2),
        maxWidth: '260px',
        '& .MuiOutlinedInput-root': {
            height: '36px',
        },
        '& .MuiInputBase-input': {
            padding: '6px 12px',
            fontSize: '0.85rem',
        },
    },
    quickSearchWrapper: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
        marginBottom: theme.spacing(0.75),
        maxWidth: '500px',
    },
    quickSearchField: {
        width: '200px',
        flexShrink: 0,
        backgroundColor: '#f5f5f5',
        '& .MuiOutlinedInput-root': {
            backgroundColor: '#f5f5f5',
            height: '34px',
            fontSize: '0.85rem',
        },
        '& .MuiInputBase-input': {
            padding: '6px 10px',
            fontSize: '0.85rem',
        },
    },
    searchButton: {
        minWidth: '64px',
        height: '34px',
        textTransform: 'none',
        fontSize: '0.82rem',
        paddingLeft: theme.spacing(1.25),
        paddingRight: theme.spacing(1.25),
    },
    advancedToggle: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        color: '#000',
        fontSize: '0.875rem',
        marginBottom: 0,
        '&:hover': {
            color: '#000',
        },
    },
    advancedSection: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        alignItems: 'center',
        backgroundColor: '#f9f9f9',
        padding: theme.spacing(2),
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(0.25),
    },
    // Compact fields/buttons for the advanced section only — same pattern as NO04Search.
    advancedTextField: {
        width: '160px',
        flexShrink: 0,
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-root': {
            height: '34px',
            fontSize: '0.85rem',
        },
        '& .MuiInputBase-input': {
            padding: '6px 10px',
            fontSize: '0.85rem',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#1976d2',
        },
    },
    advancedButton: {
        minWidth: '72px',
        height: '34px',
        textTransform: 'none',
        fontSize: '0.82rem',
        paddingLeft: theme.spacing(1.25),
        paddingRight: theme.spacing(1.25),
    },
    dateField: {
        width: '160px',
        flexShrink: 0,
        backgroundColor: '#fff',
        '& .MuiOutlinedInput-root': {
            height: '34px',
            fontSize: '0.85rem',
        },
        '& .MuiInputBase-input': {
            padding: '6px 10px',
            fontSize: '0.85rem',
        },
        '& .MuiInputLabel-root': { fontSize: '0.85rem' },
        '& .MuiInputLabel-shrink': { fontSize: '0.85rem' },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#000',
        },
        '& .MuiInputLabel-root.Mui-focused': {
            color: '#1976d2',
        },
    },
}));

export default function NO03Search() {
    const { classes } = useStyles();
    const dispatch = useDispatch();

    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [searchError, setSearchError] = useState('');

    // Internal UI state - more flexible for user input
    const [searchState, setSearchState] = useState({
        companyId: '',
        quickSearch: '',
        quickSearchType: 'invoice' as 'invoice' | 'order',
        customerNumber: '',
        amount: '',
        fromDate: '',
        toDate: '',
    });

    const firmaList: FirmNavnId[] = useSelector((state: AppState) => state.NO03.firmaList);
    const loading = useSelector((state: AppState) => state.NO03.loading);
    const userJWT = useSelector((state: AppState) => state.auth.userJWT);
    const userDepartment = userJWT?.Department ?? '';

    useEffect(() => {
        if (firmaList.length > 0) {
            const firma = [...firmaList].sort((a, b) => a.firmID - b.firmID)[0];
            dispatch(actionsNO03.NO03SetFirma(firma));
            setSearchState((prev) => ({
                ...prev,
                companyId: firma.firmID.toString(),
            }));
        } else if (userDepartment) {
            setSearchState((prev) => ({
                ...prev,
                companyId: userDepartment.padStart(3, '0'),
            }));
        }
    }, [firmaList, userDepartment, dispatch]);

    const handleInputChange = (field: string, value: any) => {
        setSearchState({
            ...searchState,
            [field]: value,
        });
    };

    const handleFirmaChange = (firmaId: number) => {
        const firma = firmaList.find((f) => f.firmID === firmaId);
        if (firma) {
            dispatch(actionsNO03.NO03SetFirma(firma));
            // Convert firma ID to 3-character string
            const companyIdStr = firmaId.toString().padStart(3, '0');
            setSearchState({
                ...searchState,
                companyId: companyIdStr,
            });
        }
    };

    const handleSearch = () => {
        const hasFilter =
            searchState.quickSearch.trim() ||
            searchState.customerNumber.trim() ||
            searchState.amount.trim() ||
            searchState.fromDate ||
            searchState.toDate;
        if (!hasFilter) {
            setSearchError('Du må fylle inn minst ett søkefelt før du kan søke.');
            return;
        }
        setSearchError('');

        // Map UI state to backend contract
        const params: NO03SearchParams = {
            companyId: searchState.companyId.padStart(3, '0'),
        };

        // Map to invoice or order number based on selected type
        if (searchState.quickSearch) {
            if (searchState.quickSearchType === 'order') {
                params.orderNumber = searchState.quickSearch.trim();
            } else {
                params.invoiceNumber = searchState.quickSearch.trim();
            }
        }

        // Customer number - convert to number if valid
        if (searchState.customerNumber) {
            const custNum = parseInt(searchState.customerNumber, 10);
            if (!isNaN(custNum)) {
                params.customerNumber = custNum;
            }
        }

        // Amount - a lone "-" means "credit notes only" (all negative amounts); otherwise
        // convert to a number if valid.
        if (searchState.amount) {
            const trimmedAmount = searchState.amount.trim();
            if (trimmedAmount === '-') {
                params.negativeOnly = true;
            } else {
                const amountNum = parseFloat(trimmedAmount);
                if (!isNaN(amountNum)) {
                    params.amount = amountNum;
                }
            }
        }

        // Date range
        if (searchState.fromDate) {
            params.fromDate = searchState.fromDate;
        }
        if (searchState.toDate) {
            params.toDate = searchState.toDate;
        }

        dispatch(actionsNO03.NO03SearchRequest(params));
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleClear = () => {
        setSearchState({
            companyId: searchState.companyId,
            quickSearch: '',
            quickSearchType: 'invoice',
            customerNumber: '',
            amount: '',
            fromDate: '',
            toDate: '',
        });
        setSearchError('');
        dispatch(actionsNO03.NO03ClearResults());
    };

    return (
        <ThemeProvider theme={neutralTheme}>
        <Paper className={classes.searchContainer} elevation={2}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                    <Typography variant="h5" className={classes.title}>
                        Fakturakopi
                    </Typography>
                    <Typography variant="body2" className={classes.subtitle}>
                        Søk og skriv ut fakturakopier
                    </Typography>
                </Box>
                <Tooltip
                    title={
                        <Box sx={{ maxWidth: 400 }}>
                            <Typography sx={{ fontSize: '0.85rem', mb: 1.5 }}>
                                Programmet brukes til å søke opp en faktura og laste ned for kopi til kunde eller annet bruk.
                            </Typography>

                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>
                                Hurtigsøk:
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', mb: 1.5 }}>
                                Øverst på siden kan du velg om du vil søke på en faktura eller en ordre. Den du velger er markert med rødt. Tast så inn faktura- eller ordrenummer og trykk på Søk eller Enter.
                            </Typography>

                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>
                                Avansert søk:
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', mb: 1.5 }}>
                                Her kan du søke på alle fakturaer på et kundenummer, på et beløp eller innenfor et datointervall. Skriv kun "-" i beløpfeltet for å vise alle kreditnotaer.
                            </Typography>

                            <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, mb: 0.5, textDecoration: 'underline' }}>
                                Søkeresultat:
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                Tabellen vises kundenummer, kundenavn, fakturanummer, faktura kid, ordrenummer, fakturanummer og fakturatotal inkl. MVA. Og deretter vises symbol for å laste ned faktura.
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                Ved å holde musepeker over ordrenummer vil du se alle ordrenummer som er med på samme faktura. Alle er søkbare i hurtigsøk feltet.
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                                Kreditnotaer vil ha rød bakgrunn i tabellen, og beløp vises med minus.
                            </Typography>
                            <Typography sx={{ fontSize: '0.85rem' }}>
                                Totalsummen for fakturaer i resultat vises øverst i tabellen.
                            </Typography>
                        </Box>
                    }
                    placement="bottom-end"
                    arrow
                >
                    <Box
                        sx={{
                            width: 22,
                            height: 22,
                            borderRadius: '50%',
                            backgroundColor: '#2e7d32',
                            color: 'white',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: 'bold',
                            userSelect: 'none',
                            flexShrink: 0,
                        }}
                    >
                        i
                    </Box>
                </Tooltip>
            </Box>

            {/* Firma Selection */}
            <Box className={classes.firmaSelectBox}>
                <FormControl fullWidth size="small">
                    <InputLabel>Velg firma</InputLabel>
                    <Select
                        value={parseInt(searchState.companyId, 10) || ''}
                        onChange={(e) => handleFirmaChange(Number(e.target.value))}
                        label="Velg firma"
                    >
                        <MenuItem value="">
                            <em>Velg firma</em>
                        </MenuItem>
                        {firmaList.length > 0
                            ? [...firmaList].sort((a, b) => a.firmID - b.firmID).map((firma) => (
                                <MenuItem key={firma.firmID} value={firma.firmID}>
                                    {firma.firmID} - {firma.firmName}
                                </MenuItem>
                            ))
                            : userDepartment ? (
                                <MenuItem value={parseInt(userDepartment, 10)}>
                                    {userDepartment}{userJWT?.CompanyName ? ` - ${userJWT.CompanyName}` : ''}
                                </MenuItem>
                            ) : null
                        }
                    </Select>
                </FormControl>
            </Box>

            {/* Quick Search with type toggle and button inline — hidden when advanced is open */}
            {!advancedOpen && (
            <Box className={classes.quickSearchWrapper}>
                <ToggleButtonGroup
                    value={searchState.quickSearchType}
                    exclusive
                    size="small"
                    onChange={(_e, val) => { if (val) handleInputChange('quickSearchType', val); }}
                    sx={{ height: '34px', flexShrink: 0 }}
                >
                    <ToggleButton
                        value="invoice"
                        sx={{
                            textTransform: 'none',
                            px: 1.5,
                            fontSize: '0.82rem',
                            '&.Mui-selected': {
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32',
                                '&:hover': { backgroundColor: '#d7edd9' },
                            },
                        }}
                    >
                        Faktura
                    </ToggleButton>
                    <ToggleButton
                        value="order"
                        sx={{
                            textTransform: 'none',
                            px: 1.5,
                            fontSize: '0.82rem',
                            '&.Mui-selected': {
                                backgroundColor: '#e8f5e9',
                                color: '#2e7d32',
                                '&:hover': { backgroundColor: '#d7edd9' },
                            },
                        }}
                    >
                        Ordre
                    </ToggleButton>
                </ToggleButtonGroup>
                <TextField
                    fullWidth
                    size="small"
                    placeholder={searchState.quickSearchType === 'order' ? 'Søk på ordrenr' : 'Søk på fakturanr'}
                    value={searchState.quickSearch}
                    onChange={(e) => handleInputChange('quickSearch', e.target.value)}
                    onKeyDown={handleKeyDown}
                    className={classes.quickSearchField}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
                <Button
                    variant="contained"
                    startIcon={<SearchIcon fontSize="small" />}
                    onClick={handleSearch}
                    disabled={loading}
                    className={classes.searchButton}
                    sx={{ backgroundColor: RED, '&:hover': { backgroundColor: RED_DARK } }}
                >
                    Søk
                </Button>
            </Box>
            )}

            {searchError ? (
                <Alert severity="error" sx={{ mb: 2, maxWidth: '560px' }} onClose={() => setSearchError('')}>
                    {searchError}
                </Alert>
            ) : null}

            {/* Advanced Search Toggle */}
            <Box
                className={classes.advancedToggle}
                onClick={() => setAdvancedOpen(!advancedOpen)}
            >
                {advancedOpen ? <ExpandLess /> : <ExpandMore />}
                <Typography variant="body2" sx={{ ml: 0.5 }}>
                    {advancedOpen ? 'Tilbake til hurtigsøk' : 'Avansert søk'}
                </Typography>
            </Box>

            {/* Advanced Search Section — same compact flex-wrap pattern as NO04Search */}
            <Collapse in={advancedOpen}>
                <Box className={classes.advancedSection}>
                    <TextField
                        size="small"
                        label="Søk på kunde"
                        placeholder="F.eks. 500190"
                        value={searchState.customerNumber}
                        onChange={(e) => handleInputChange('customerNumber', e.target.value)}
                        onKeyDown={handleKeyDown}
                        className={classes.advancedTextField}
                    />
                    <TextField
                        size="small"
                        label="Søk på beløp"
                        placeholder="10999, eller - for kun kreditnotaer"
                        value={searchState.amount}
                        onChange={(e) => handleInputChange('amount', e.target.value)}
                        onKeyDown={handleKeyDown}
                        className={classes.advancedTextField}
                    />
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                        <DatePicker
                            label="Fra dato"
                            format="DD.MM.YYYY"
                            value={searchState.fromDate ? dayjs(searchState.fromDate) : null}
                            onChange={(date: Dayjs | null) => {
                                handleInputChange('fromDate', date ? date.format('YYYY-MM-DD') : '');
                            }}
                            slotProps={{
                                textField: { size: 'small', placeholder: 'Velg dato', className: classes.dateField },
                            }}
                        />
                    </LocalizationProvider>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="nb">
                        <DatePicker
                            label="Til dato"
                            format="DD.MM.YYYY"
                            value={searchState.toDate ? dayjs(searchState.toDate) : null}
                            onChange={(date: Dayjs | null) => {
                                handleInputChange('toDate', date ? date.format('YYYY-MM-DD') : '');
                            }}
                            slotProps={{
                                textField: { size: 'small', placeholder: 'Velg dato', className: classes.dateField },
                            }}
                        />
                    </LocalizationProvider>
                    <Button
                        variant="contained"
                        startIcon={<SearchIcon fontSize="small" />}
                        onClick={handleSearch}
                        disabled={loading}
                        className={classes.advancedButton}
                        sx={{ backgroundColor: RED, '&:hover': { backgroundColor: RED_DARK } }}
                    >
                        Søk
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleClear}
                        className={classes.advancedButton}
                        sx={{ borderColor: RED, color: RED, '&:hover': { borderColor: RED_DARK, backgroundColor: 'rgba(171,39,59,0.04)' } }}
                    >
                        Nullstill
                    </Button>
                </Box>
            </Collapse>
        </Paper>
        </ThemeProvider>
    );
}
